import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import { get } from 'lodash';
import moment from 'moment';
import fastMemoize from 'fast-memoize';
import styled from 'styled-components';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import { LoadingTile } from 'components/molecules/ErrorTile';
import { fetchList, resetPassword } from 'services/users/duck';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import Pagination from 'components/molecules/tables/components/Pagination';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ActiveStatusLabel from 'components/atoms/labels/ActiveStatusLabel';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 25;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => row[filter.id];
const memoizeFilterValue = fastMemoize(generateFilterValue);

const ResetLink = styled.a`
    float: right;
    cursor: pointer;
`;

class UsersTableContainer extends Component {
    constructor(props) {
        super(props);

        this.activeSearch = false;
        this.state = {
            showPagination: false,

            currentUrl: props.parsedUrl.href,
            list: null,

            // controlled table
            // filtered: [],
            // sorted: [],
            // page: 0,
            // pageSize: 25
            ...UsersTableContainer.convertUriToState(props.parsedUrl)
        };
        this.userTableInstance = React.createRef();
        this.mirrorStateToUri = this.mirrorStateToUri.bind(this);
        this.getColumns = this.getColumns.bind(this);
        this.actionsFormatter = this.actionsFormatter.bind(this);
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    componentDidMount() {
        this.props.fetchList();
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    getUserTableInstance() {
        if (!this.userTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.userTableInstance;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.actionsFormatter(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 50,
                resizable: false,
                sortable: false
            }, {
                Header: 'NAME',
                Cell: props => this.userFullNameLink(props),
                accessor: 'fullName',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control userName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="User Name"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'EMAIL',
                // Cell: props => this.nameLink(null, props.original),
                accessor: 'email',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control email-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="User Email"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }, {
                Header: 'LAST LOGIN',
                accessor: 'lastLoginAt',
                Cell: (props) => {
                    const result = ['Never Logged In'];
                    if (props.value) {
                        result[0] = this.dateFormatter(props.value, props.original);
                    }

                    if (props.original.resetState !== 'complete') {
                        let title = 'Pending password reset';
                        if (props.original.resetState === 'pending-invite') {
                            title = 'Pending invitation';
                        }
                        result.push((<span key={2} style={{ marginLeft: '5px' }} title={title} className="icon-mail-wait-fill" />));
                    }

                    return result;
                },
                maxWidth: 150
            }, {
                Header: 'ACTIVE',
                accessor: 'isActive',
                Cell: props => <CellDiv value={<ActiveStatusLabel isActive={props.value} />} style={{ textAlign: 'center' }} />,
                maxWidth: 100
            }, {
                Header: 'CREATED',
                accessor: 'createdAt',
                Cell: props => <CellDiv value={this.dateFormatter(props.value)} type="date" style={{ textAlign: 'right' }} />,
                maxWidth: 150
            }
        ];
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'users.list', []);
        const propUrl = get(props, 'parsedUrl', {});
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (state.currentUrl !== propUrl.href) {
            mergeList.push({
                currentUrl: propUrl.href,
                ...UsersTableContainer.convertUriToState(propUrl)
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getUserTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page }, () => this.mirrorStateToUri());
    }

    dateFormatter(cell) {
        const date = moment(cell);
        return (<span key={1} title={date.format('MM-DD-YYYY HH:MM:SS')}>{date.format('MM-DD-YYYY')}</span>);
    }

    actionsFormatter(cell, row) {
        const links = [(
            <Link key={0} title="Edit user" to={`/admin/users/${row.id}/edit`} >
                <span className="icon-edit" />
            </Link>
        )];

        const isAllowed = this.props.users.currentUser &&
            this.props.users.currentUser.scope.indexOf('User-ResetPassword') > -1;

        if (!row.isOkta && isAllowed) {
            let altText = 'Reset user\'s password and send them an email with instructions on how to set their new password';
            if (!row.lastLoginAt) {
                altText = 'Send this user an invitation email';
            }
            const resetClick = () => this.props.resetPassword({ user: row });
            links.push((
                <ResetLink key={1} title={altText} onClick={resetClick}>
                    <span className="icon-email" />
                </ResetLink>
            ));
        }

        return links;
    }

    userFullNameLink(props) {
        return (
            <Link title="Edit user" to={`/admin/users/${props.original.id}`} >
                {props.value}
            </Link>
        );
    }

    mirrorStateToUri() {
        const query = {};

        this.state.filtered.forEach((filter) => {
            switch (filter.id) {
            case 'fullName':
                if (filter.value) {
                    query.userName = filter.value;
                }
                break;
            case 'email':
                if (filter.value) {
                    query.userEmail = filter.value;
                }
                break;

            default:
                // ignore
            }
        });

        if (this.state.page !== DEFAULT_PAGE) {
            query.page = this.state.page;
        }

        if (this.state.pageSize !== DEFAULT_PAGE_SIZE) {
            query.pageSize = this.state.pageSize;
        }

        if (this.state.sorted.length) {
            const sortedItem = this.state.sorted[0];

            query.sort = sortedItem.id;
            query.sortDir = sortedItem.desc ? 'desc' : 'asc';
        }

        this.props.parsedUrl.set('query', query);
        this.props.push(this.props.parsedUrl.toString({ onlyPath: true }));
    }

    static convertUriToState(url) {
        const filtered = [];
        const sorted = [];
        const { query } = url;

        if (query.userName) {
            filtered.push({ id: 'fullName', value: query.userName });
        }

        if (query.userEmail) {
            filtered.push({ id: 'email', value: query.userEmail });
        }

        if (query.sort) {
            sorted.push({ id: query.sort, desc: query.sortDir === 'desc' });
        }

        let page = Number(query.page);
        if (!page || page < 0) {
            page = DEFAULT_PAGE;
        }

        let pageSize = Number(query.pageSize);
        if (!pageSize || pageSize < 1) {
            pageSize = DEFAULT_PAGE_SIZE;
        }

        return {
            filtered,
            sorted,
            page,
            pageSize
        };
    }

    render() {
        const data = this.props.users.list;
        const dataFetched = this.props.users.listFetched;

        return (
            <Grid fluid>
                <ButtonStandardPrimary
                    type="submit"
                    onClick={() => this.props.push('/admin/users/create')}>
                    Create User
                </ButtonStandardPrimary>

                <br />
                <br />
                {dataFetched ? (
                    <StyledReactTable
                        data={data}
                        ref={this.userTableInstance}
                        defaultPageSize={25}
                        minRows={10}
                        filtered={this.state.filtered}
                        sorted={this.state.sorted}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        onFilteredChange={(filtered, column, value) => {
                            this.onFilteredChangeCustom(value, column.id || column.accessor);
                        }}
                        onSortedChange={sorted => this.setState({ sorted }, () => this.mirrorStateToUri())}
                        onPageChange={page => this.setState({ page }, () => this.mirrorStateToUri())}
                        onPageSizeChange={(pageSize, page) => this.setState({
                            page, pageSize
                        }, () => this.mirrorStateToUri())}
                        getTheadThProps={() => ({
                            style: {
                                backgroundColor: '#E2E2E2',
                            }
                        })}
                        getTheadFilterThProps={() => ({
                            style: {
                                overflow: 'inherit' // allows react-select to work
                            }
                        })}
                        columns={this.getColumns()}
                        defaultSorted={[{
                            id: 'fullName', desc: false
                        }]}
                        className="-highlight"
                        showPagination={this.state.showPagination}
                        PaginationComponent={Pagination}
                    />
                ) : (
                    <LoadingTile />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    users: state.users,
    parsedUrl: state.router.parsedUrl
});

export default connect(mapStateToProps, { fetchList, push, resetPassword })(UsersTableContainer);



// WEBPACK FOOTER //
// ./src/containers/tables/UsersTableContainer.js